import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <Trans i18nKey="about.hero.title">
                About <span className="font-brand">tvia.ai</span>
              </Trans>
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto opacity-90">
              {t('about.hero.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('about.mission.title')}
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                <Trans i18nKey="about.mission.description">
                  At <span className="font-brand">tvia.ai</span>, we believe that everyone deserves access to quality legal assistance, regardless of their background or financial situation. Our AI-powered platform breaks down barriers to justice by making legal services more accessible, affordable, and efficient.
                </Trans>
              </p>
              <p className="text-lg text-gray-700">
                {t('about.mission.vision')}
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                {t('about.mission.values.title')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-cyan-600 font-bold mr-3">✓</span>
                  <span className="text-gray-700">{t('about.mission.values.accessibility')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-600 font-bold mr-3">✓</span>
                  <span className="text-gray-700">{t('about.mission.values.transparency')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-600 font-bold mr-3">✓</span>
                  <span className="text-gray-700">{t('about.mission.values.innovation')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-600 font-bold mr-3">✓</span>
                  <span className="text-gray-700">{t('about.mission.values.empowerment')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Technology Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('about.technology.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.technology.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="bg-cyan-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('about.technology.ai.title')}
              </h3>
              <p className="text-gray-600">
                {t('about.technology.ai.description')}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-cyan-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('about.technology.multilingual.title')}
              </h3>
              <p className="text-gray-600">
                {t('about.technology.multilingual.description')}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-cyan-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('about.technology.security.title')}
              </h3>
              <p className="text-gray-600">
                {t('about.technology.security.description')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('about.team.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.team.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Yoav - General Employee (does everything) */}
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <img 
                src="https://drive.google.com/thumbnail?id=1AbMzwSIYf-AE06HhUUrlaVQKTanfnuaI&sz=w400" 
                alt="Yoav Yanovski" 
                className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA5NiA5NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDgiIGN5PSI0OCIgcj0iNDgiIGZpbGw9IiNEMzAwMDAiLz4KPHN2ZyB4PSIyNCIgeT0iMjQiIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJ3aGl0ZSI+CjxwYXRoIGQ9Ik0xMiAxMmMyLjIxIDAgNC0xLjc5IDQtNHMtMS43OS00LTQtNC00IDEuNzktNCA0IDEuNzkgNCA0IDR6bTAgMmMtMi42NyAwLTggMS4zNC04IDR2MmgxNnYtMmMwLTIuNjYtNS4zMy00LTgtNHoiLz4KPC9zdmc+Cjwvc3ZnPgo=';
                }}
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('about.team.yoav.name')}
              </h3>
              <p className="text-cyan-600 font-medium mb-3">
                {t('about.team.yoav.title')}
              </p>
              <p className="text-gray-600 text-sm">
                {t('about.team.yoav.bio')}
              </p>
            </div>

            {/* Shlomit - Wife */}
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <img 
                src="https://drive.google.com/thumbnail?id=17TElNTzCZOjKlc21cAqA4AM3J3vpw49k&sz=w400" 
                alt="Shlomit Yanovski" 
                className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA5NiA5NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDgiIGN5PSI0OCIgcj0iNDgiIGZpbGw9IiNEMzAwMDAiLz4KPHN2ZyB4PSIyNCIgeT0iMjQiIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJ3aGl0ZSI+CjxwYXRoIGQ9Ik0xMiAxMmMyLjIxIDAgNC0xLjc5IDQtNHMtMS43OS00LTQtNC00IDEuNzktNCA0IDEuNzkgNCA0IDR6bTAgMmMtMi42NyAwLTggMS4zNC04IDR2MmgxNnYtMmMwLTIuNjYtNS4zMy00LTgtNHoiLz4KPC9zdmc+Cjwvc3ZnPgo=';
                }}
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('about.team.shlomit.name')}
              </h3>
              <p className="text-cyan-600 font-medium mb-3">
                {t('about.team.shlomit.title')}
              </p>
              <p className="text-gray-600 text-sm">
                {t('about.team.shlomit.bio')}
              </p>
            </div>

            {/* Tom - 5 years old */}
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <img 
                src="https://drive.google.com/thumbnail?id=1bYOJ0ZaY65MO6Fi6FdgB9Ig4bXaF6d1Y&sz=w400" 
                alt="Tom Yanovski" 
                className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA5NiA5NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDgiIGN5PSI0OCIgcj0iNDgiIGZpbGw9IiNEMzAwMDAiLz4KPHN2ZyB4PSIyNCIgeT0iMjQiIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJ3aGl0ZSI+CjxwYXRoIGQ9Ik0xMiAxMmMyLjIxIDAgNC0xLjc5IDQtNHMtMS43OS00LTQtNC00IDEuNzktNCA0IDEuNzkgNCA0IDR6bTAgMmMtMi42NyAwLTggMS4zNC04IDR2MmgxNnYtMmMwLTIuNjYtNS4zMy00LTgtNHoiLz4KPC9zdmc+Cjwvc3ZnPgo=';
                }}
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('about.team.tom.name')}
              </h3>
              <p className="text-cyan-600 font-medium mb-3">
                {t('about.team.tom.title')}
              </p>
              <p className="text-gray-600 text-sm">
                {t('about.team.tom.bio')}
              </p>
            </div>

            {/* Roy - 3 years old */}
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <img 
                src="https://drive.google.com/thumbnail?id=10SDssNQdpIjW5Qk0pCiP5hjnBrldPjpm&sz=w400" 
                alt="Roy Yanovski" 
                className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA5NiA5NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDgiIGN5PSI0OCIgcj0iNDgiIGZpbGw9IiNEMzAwMDAiLz4KPHN2ZyB4PSIyNCIgeT0iMjQiIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJ3aGl0ZSI+CjxwYXRoIGQ9Ik0xMiAxMmMyLjIxIDAgNC0xLjc5IDQtNHMtMS43OS00LTQtNC00IDEuNzktNCA0IDEuNzkgNCA0IDR6bTAgMmMtMi42NyAwLTggMS4zNC04IDR2MmgxNnYtMmMwLTIuNjYtNS4zMy00LTgtNHoiLz4KPC9zdmc+Cjwvc3ZnPgo=';
                }}
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('about.team.roy.name')}
              </h3>
              <p className="text-cyan-600 font-medium mb-3">
                {t('about.team.roy.title')}
              </p>
              <p className="text-gray-600 text-sm">
                {t('about.team.roy.bio')}
              </p>
            </div>

            {/* Chewie - The Dog */}
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <img 
                src="https://drive.google.com/thumbnail?id=1azvvZWwXRFfqEuOfMqZqDjYfFXchvxP8&sz=w400" 
                alt="Chewie" 
                className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA5NiA5NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDgiIGN5PSI0OCIgcj0iNDgiIGZpbGw9IiNEMzAwMDAiLz4KPHN2ZyB4PSIyNCIgeT0iMjQiIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJ3aGl0ZSI+CjxwYXRoIGQ9Ik0xMiAxMmMyLjIxIDAgNC0xLjc5IDQtNHMtMS43OS00LTQtNC00IDEuNzktNCA0IDEuNzkgNCA0IDR6bTAgMmMtMi42NyAwLTggMS4zNC04IDR2MmgxNnYtMmMwLTIuNjYtNS4zMy00LTgtNHoiLz4KPC9zdmc+Cjwvc3ZnPgo=';
                }}
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('about.team.chewie.name')}
              </h3>
              <p className="text-cyan-600 font-medium mb-3">
                {t('about.team.chewie.title')}
              </p>
              <p className="text-gray-600 text-sm">
                {t('about.team.chewie.bio')}
              </p>
            </div>
          </div>

          {/* Family Business Note */}
          <div className="mt-12 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('about.team.familyBusiness.title')}
            </h3>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              <Trans i18nKey="about.team.familyBusiness.description">
                At <span className="font-brand">tvia.ai</span>, we believe in keeping it in the family. From the CEO who does everything (Yoav) to our youngest team members who provide endless entertainment and occasional chaos, we're proof that great things can come in small, noisy packages. Our dog Chewie even helps with quality assurance by testing our stress levels daily.
              </Trans>
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {t('about.cta.title')}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {t('about.cta.subtitle')}
          </p>
          <div className="space-x-4">
            <Link
              to="/file-lawsuit"
              className="bg-white text-cyan-600 px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors"
            >
              {t('about.cta.getStarted')}
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-md font-semibold hover:bg-white hover:text-cyan-600 transition-colors"
            >
              {t('about.cta.contactUs')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;